<template>
    <v-dialog :value="value" max-width="920px" persistent scrollable @input="$emit('input', $event)">
        <v-card class="acd">
            <!-- Header -->
            <div class="acd-header">
                <span class="acd-title">{{ isEdit ? "Edit company" : "Add new company" }}</span>
                <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
            </div>

            <!-- Stepper (โหมด edit ไม่มีขั้น User information) -->
            <div class="acd-stepper">
                <template v-for="(item, i) in stepItems">
                    <div :key="'s' + item.n" class="acd-step" :class="{ clickable: item.n < step }"
                        @click="item.n < step && (step = item.n)">
                        <div class="acd-step-circle" :class="{ done: item.n < step, active: item.n === step }">
                            <v-icon v-if="item.n < step" small color="white">mdi-check</v-icon>
                            <span v-else>{{ i + 1 }}</span>
                        </div>
                        <span class="acd-step-label" :class="{ active: item.n === step }">{{ item.label }}</span>
                    </div>
                    <div v-if="i < stepItems.length - 1" :key="'l' + item.n" class="acd-step-line"
                        :class="{ done: item.n < step }"></div>
                </template>
            </div>

            <v-card-text class="acd-body">
                <!-- ===================== STEP 1: User information ===================== -->
                <div v-show="step === 1">
                    <h2 class="acd-h2">User information</h2>
                    <p class="acd-sub">Create the primary administrator account for this company.</p>
                    <v-row dense>
                        <v-col cols="12" md="6" class="acd-field">
                            <label>First name <span class="req">*</span></label>
                            <v-text-field v-model="user.Firstname" ref="Firstname" outlined dense
                                hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6" class="acd-field">
                            <label>Last name <span class="req">*</span></label>
                            <v-text-field v-model="user.Lastname" ref="Lastname" outlined dense
                                hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6" class="acd-field">
                            <label>Email <span class="req">*</span></label>
                            <v-text-field v-model="user.Email" ref="Email" :rules="emailRules" outlined dense
                                hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6" class="acd-field">
                            <label>Phone number <span class="req">*</span></label>
                            <v-text-field v-model="user.User_Telephone_Number" ref="user_phone" :rules="phoneRules"
                                maxlength="10" outlined dense hide-details="auto"
                                @keypress="isNumber($event)"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6" class="acd-field">
                            <label>Username <span class="req">*</span></label>
                            <v-text-field v-model="user.Username" ref="Username" outlined dense
                                hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6" class="d-flex align-end">
                            <div class="acd-info">
                                <v-icon color="#3b82f6" class="mr-3">mdi-information-outline</v-icon>
                                Used to sign in to the account.
                            </div>
                        </v-col>
                        <v-col cols="12" md="6" class="acd-field">
                            <label>Password <span class="req">*</span></label>
                            <v-text-field v-model="user.Password" ref="Password" :type="showPass ? 'text' : 'password'"
                                :append-icon="showPass ? 'mdi-eye-outline' : 'mdi-eye-off-outline'" outlined dense
                                hide-details="auto" @click:append="showPass = !showPass"></v-text-field>
                            <div class="acd-hint">Use at least 8 characters with a number and symbol.</div>
                        </v-col>
                        <v-col cols="12" md="6" class="acd-field">
                            <label>Confirm password <span class="req">*</span></label>
                            <v-text-field v-model="user.Confirm_Password" ref="Confirm_Password"
                                :type="showConfirm ? 'text' : 'password'"
                                :append-icon="showConfirm ? 'mdi-eye-outline' : 'mdi-eye-off-outline'" outlined dense
                                hide-details="auto" @click:append="showConfirm = !showConfirm"></v-text-field>
                        </v-col>
                    </v-row>
                </div>

                <!-- ===================== STEP 2: Company information ===================== -->
                <div v-show="step === 2">
                    <h2 class="acd-h2">Company information</h2>
                    <p class="acd-sub">Enter the company's legal name, address, and contact details.</p>

                    <div class="acd-section-title">Company details</div>
                    <v-row dense>
                        <v-col cols="12" md="8" class="acd-field">
                            <label>Company name <span class="req">*</span></label>
                            <v-text-field v-model="com.name" ref="Company_Name_Add" outlined dense
                                hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4" class="acd-field">
                            <label class="d-flex align-center">
                                Company code <span class="req ml-1">*</span>
                                <v-tooltip bottom max-width="320">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon small color="#3b82f6" class="ml-2" v-bind="attrs"
                                            v-on="on">mdi-help-circle</v-icon>
                                    </template>
                                    <span>Employees use this code to identify your company when signing in, so keep it
                                        unique and easy to recognise.</span>
                                </v-tooltip>
                            </label>
                            <v-text-field v-model="com.code" ref="Company_Code_Add" maxlength="6" :rules="comcodeRules"
                                :disabled="isEdit" outlined dense hide-details="auto"></v-text-field>
                            <div class="acd-hint">{{ codeHint }}</div>
                        </v-col>
                    </v-row>

                    <div class="acd-section-title mt-3">Company address</div>
                    <v-row dense>
                        <v-col cols="12" class="acd-field">
                            <label>Address line 1 <span class="req">*</span></label>
                            <v-text-field v-model="com.company_address" ref="Company_Address_Add" outlined dense
                                hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" class="acd-field">
                            <label>Address line 2</label>
                            <v-text-field v-model="com.company_address2" outlined dense
                                hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4" class="acd-field">
                            <label>Sub-district</label>
                            <v-text-field v-model="com.subdistrict" ref="subdistrict" outlined dense
                                hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4" class="acd-field">
                            <label>District</label>
                            <v-text-field v-model="com.district" ref="district" outlined dense
                                hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4" class="acd-field">
                            <label>Province</label>
                            <v-text-field v-model="com.province" ref="province" outlined dense
                                hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4" class="acd-field">
                            <label>Postcode</label>
                            <v-text-field v-model="com.postcode" ref="postcode" outlined dense
                                hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4" class="acd-field">
                            <label>Country <span class="req">*</span></label>
                            <v-select v-model="com.country" ref="country" :items="countryList"
                                placeholder="Select a country" outlined dense hide-details="auto"></v-select>
                        </v-col>
                        <v-col cols="12" md="4" class="acd-field">
                            <label>Branch</label>
                            <v-text-field v-model="com.company_branch" ref="Company_Branch_Add" outlined dense
                                hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6" class="acd-field">
                            <label>Company email</label>
                            <v-text-field v-model="com.company_email" ref="Company_Email_Add" :rules="emailRules"
                                outlined dense hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6" class="acd-field">
                            <label>Telephone number</label>
                            <v-text-field v-model="com.company_tel" ref="Company_Telephone_Number_Add" :rules="telRules"
                                outlined dense hide-details="auto"></v-text-field>
                        </v-col>
                    </v-row>
                </div>

                <!-- ===================== STEP 3: Billing ===================== -->
                <div v-show="step === 3">
                    <h2 class="acd-h2">Billing &amp; employee settings</h2>
                    <p class="acd-sub">Configure invoice details and employee ID generation.</p>
                    <v-row>
                        <!-- Invoice -->
                        <v-col cols="12" md="7">
                            <div class="acd-panel">
                                <div class="acd-panel-title">Invoice information</div>
                                <v-checkbox v-model="sameAs" label="Same as company information" color="#2a7f86" dense
                                    hide-details class="mt-0 mb-1"></v-checkbox>

                                <div class="acd-invoice-scroll">
                                    <template v-if="sameAs">
                                        <div class="acd-hint mb-3">
                                            Company name, address, email, telephone, country, branch and postcode will
                                            be
                                            copied automatically.
                                        </div>
                                        <div class="acd-summary">
                                            <div class="font-weight-bold">{{ com.name || "-" }}</div>
                                            <div>{{ companyAddressLine || "-" }}</div>
                                            <div>{{ com.company_email || "-" }} · {{ com.company_tel || "-" }}</div>
                                        </div>
                                        <a class="acd-link" @click="sameAs = false">Use different billing details</a>
                                        <v-divider class="my-4"></v-divider>
                                    </template>
                                    <div v-else class="acd-hint mb-3">Enter separate company, address and contact
                                        details
                                        for billing.
                                    </div>

                                    <v-row dense>
                                        <v-col v-show="!sameAs" cols="12" md="6" class="acd-field">
                                            <label>Company name <span class="req">*</span></label>
                                            <v-text-field v-model="com.inv_company_name" ref="INV_Company_Name_Add"
                                                outlined dense hide-details="auto"></v-text-field>
                                        </v-col>
                                        <v-col cols="12" md="6" class="acd-field">
                                            <label>Company name (Thai)</label>
                                            <v-text-field v-model="com.inv_company_name_th"
                                                ref="INV_Company_Name_TH_Add" outlined dense
                                                hide-details="auto"></v-text-field>
                                        </v-col>
                                        <v-col v-show="!sameAs" cols="12" class="acd-field">
                                            <label>Invoice address line 1 <span class="req">*</span></label>
                                            <v-text-field v-model="com.inv_company_address" ref="Invoice_Address_Add"
                                                outlined dense hide-details="auto"></v-text-field>
                                        </v-col>
                                        <v-col v-show="!sameAs" cols="12" class="acd-field">
                                            <label>Invoice address line 2</label>
                                            <v-text-field v-model="com.inv_company_address2" outlined dense
                                                hide-details="auto"></v-text-field>
                                        </v-col>
                                        <v-col v-show="!sameAs" cols="12" md="6" class="acd-field">
                                            <label>Sub-district</label>
                                            <v-text-field v-model="com.inv_subdistrict" ref="inv_subdistrict" outlined
                                                dense hide-details="auto"></v-text-field>
                                        </v-col>
                                        <v-col v-show="!sameAs" cols="12" md="6" class="acd-field">
                                            <label>District</label>
                                            <v-text-field v-model="com.inv_district" ref="inv_district" outlined dense
                                                hide-details="auto"></v-text-field>
                                        </v-col>
                                        <v-col v-show="!sameAs" cols="12" md="6" class="acd-field">
                                            <label>Province</label>
                                            <v-combobox v-model="com.inv_province" ref="inv_province"
                                                :items="provinceList" outlined dense hide-details="auto"></v-combobox>
                                        </v-col>
                                        <v-col v-show="!sameAs" cols="12" md="6" class="acd-field">
                                            <label>Postcode</label>
                                            <v-text-field v-model="com.inv_postcode" ref="inv_postcode" outlined dense
                                                hide-details="auto"></v-text-field>
                                        </v-col>
                                        <v-col v-show="!sameAs" cols="12" md="6" class="acd-field">
                                            <label>Country <span class="req">*</span></label>
                                            <v-combobox v-model="com.inv_country" ref="inv_country" :items="countryList"
                                                outlined dense hide-details="auto"></v-combobox>
                                        </v-col>
                                        <v-col v-show="!sameAs" cols="12" md="6" class="acd-field">
                                            <label>Branch</label>
                                            <v-text-field v-model="com.inv_company_branch" ref="INV_Branch_Add" outlined
                                                dense hide-details="auto"></v-text-field>
                                        </v-col>
                                        <v-col v-show="!sameAs" cols="12" md="6" class="acd-field">
                                            <label>Company email</label>
                                            <v-text-field v-model="com.inv_company_email" ref="INV_Email_Add"
                                                :rules="emailRules" outlined dense hide-details="auto"></v-text-field>
                                        </v-col>
                                        <v-col v-show="!sameAs" cols="12" md="6" class="acd-field">
                                            <label>Telephone number</label>
                                            <v-text-field v-model="com.inv_company_tel" ref="INV_Telephone_Number_Add"
                                                :rules="telRules" outlined dense hide-details="auto"></v-text-field>
                                        </v-col>
                                        <v-col cols="12" md="6" class="acd-field">
                                            <label>Tax ID <span class="req">*</span></label>
                                            <v-text-field v-model="com.inv_tax_id" ref="INV_Tax_ID_Add" outlined dense
                                                hide-details="auto"></v-text-field>
                                        </v-col>
                                        <v-col cols="12" md="6" class="acd-field">
                                            <label>Status</label>
                                            <v-select v-model="com.status" :items="statusall" :disabled="disabledStatus"
                                                outlined dense hide-details></v-select>
                                        </v-col>
                                    </v-row>
                                    <div v-if="sameAs" class="acd-hint mt-3">
                                        Turn off "Same as company information" to edit the full invoice address.
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Employee ID -->
                        <v-col cols="12" md="5">
                            <div class="acd-panel">
                                <div class="acd-panel-title">Employee ID settings</div>
                                <div class="acd-field">
                                    <label>Assign employee ID</label>
                                    <v-select v-model="com.doc_status" :items="emp_assign" item-text="name"
                                        item-value="values" :disabled="disabledStatus" outlined dense
                                        hide-details></v-select>
                                </div>
                                <template v-if="com.doc_status == 0">
                                    <div class="acd-field mt-4">
                                        <label>Prefix</label>
                                        <v-text-field v-model="com.id_prefix" outlined dense
                                            hide-details></v-text-field>
                                    </div>
                                    <v-row dense class="mt-2">
                                        <v-col cols="6" class="acd-field">
                                            <label>Range</label>
                                            <v-text-field v-model="com.running_len" ref="Range" type="number" outlined
                                                dense hide-details></v-text-field>
                                        </v-col>
                                        <v-col cols="6" class="acd-field">
                                            <label>Next running</label>
                                            <v-text-field v-model="com.running_next" ref="Next Running" type="number"
                                                outlined dense hide-details></v-text-field>
                                        </v-col>
                                    </v-row>
                                    <div class="acd-preview mt-4">
                                        <div class="acd-hint">Employee ID preview</div>
                                        <div class="acd-preview-id">{{ employeeIdPreview }}</div>
                                    </div>
                                    <div class="acd-hint mt-2">New employee IDs will continue from this number.</div>
                                </template>
                                <div v-else class="acd-hint mt-3">Employee IDs will be entered manually when adding
                                    employees.</div>
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <!-- ===================== STEP 4: Menu access ===================== -->
                <div v-show="step === 4">
                    <h2 class="acd-h2">Menu access</h2>
                    <p class="acd-sub">{{ menuSubtitle }}</p>

                    <div class="d-flex align-center flex-wrap mb-4" style="gap: 16px;">
                        <v-text-field v-model="menuSearch" prepend-inner-icon="mdi-magnify"
                            placeholder="Search menu or submenu" outlined dense hide-details clearable
                            style="max-width: 340px;"></v-text-field>
                        <v-checkbox v-model="allMenusSelected" label="Select all access" color="#2a7f86" dense
                            hide-details class="mt-0"></v-checkbox>
                        <v-spacer></v-spacer>
                        <div class="acd-badge">
                            {{ selectedGroupCount }} groups · {{ selectedDetailIds.length }} submenus selected
                        </div>
                    </div>

                    <div class="acd-menu">
                        <!-- Group list -->
                        <div class="acd-menu-left">
                            <div class="acd-menu-head">Group menus</div>
                            <div class="acd-menu-scroll">
                                <div v-if="loadingMenus" class="pa-4 acd-hint">Loading menus...</div>
                                <div v-for="g in filteredGroups" :key="g.id" class="acd-group-row"
                                    :class="{ active: activeGroupId === g.id }" @click="activeGroupId = g.id">
                                    <div @click.stop>
                                        <v-simple-checkbox :value="groupState(g) === 'all'"
                                            :indeterminate="groupState(g) === 'some'" color="#2a7f86" :ripple="false"
                                            @input="toggleGroup(g)"></v-simple-checkbox>
                                    </div>
                                    <span class="acd-group-name">{{ g.cgm_name }}</span>
                                    <span class="acd-count">{{ groupSelectedCount(g) }}/{{ groupTotalCount(g) }}</span>
                                </div>
                                <div v-if="!loadingMenus && filteredGroups.length === 0" class="pa-4 acd-hint">No menus
                                    found.</div>
                            </div>
                        </div>

                        <!-- Submenus of active group -->
                        <div class="acd-menu-right">
                            <div class="acd-menu-head">Submenus in {{ activeGroup ? activeGroup.cgm_name : "-" }}</div>
                            <div class="acd-menu-scroll">
                                <template v-if="activeGroup">
                                    <div class="acd-suball">
                                        <v-simple-checkbox :value="groupState(activeGroup) === 'all'"
                                            :indeterminate="groupState(activeGroup) === 'some'" color="#2a7f86"
                                            :ripple="false" @input="toggleGroup(activeGroup)"></v-simple-checkbox>
                                        <span class="ml-2 font-weight-medium">Select all in {{ activeGroup.cgm_name
                                        }}</span>
                                        <v-spacer></v-spacer>
                                        <span class="acd-count">{{ groupSelectedCount(activeGroup) }} of {{
                                            groupTotalCount(activeGroup) }}
                                            selected</span>
                                    </div>
                                    <div v-if="activeDetails.length === 0" class="pa-4 acd-hint">
                                        This group has no submenus. Tick the group to give access to it.
                                    </div>
                                    <div class="acd-subgrid">
                                        <div v-for="d in activeDetails" :key="d.id" class="acd-subitem"
                                            @click="toggleDetail(d.id)">
                                            <div @click.stop>
                                                <v-simple-checkbox :value="selectedDetailIds.includes(d.id)"
                                                    color="#2a7f86" :ripple="false"
                                                    @input="toggleDetail(d.id)"></v-simple-checkbox>
                                            </div>
                                            <span class="ml-2">{{ d.cmd_name }}</span>
                                        </div>
                                    </div>
                                </template>
                            </div>
                            <div class="acd-menu-foot">
                                Selecting a group grants access to all submenus. Select individual submenus for partial
                                access.
                            </div>
                        </div>
                    </div>
                </div>
            </v-card-text>

            <!-- Footer -->
            <div class="acd-footer">
                <span class="acd-hint">{{ footerHint }}</span>
                <!-- ⚠️ สำหรับทดสอบ: แสดงเฉพาะตอนรัน dev (npm run serve) ไม่ขึ้นบน production -->
                <v-btn v-if="isDev && !isEdit" text small color="orange darken-2" class="ml-3" @click="fillTestData">
                    <v-icon small left>mdi-flask-outline</v-icon>Fill test data
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn outlined class="acd-btn" @click="close">Cancel</v-btn>
                <v-btn v-if="step > firstStep" outlined class="acd-btn ml-3" @click="step--">Back</v-btn>
                <!-- โหมด add: Continue ทีละขั้น แล้วกด Create company ที่ขั้นสุดท้าย -->
                <template v-if="!isEdit">
                    <v-btn v-if="step < 4" depressed color="#2a7f86" class="acd-btn white--text ml-3"
                        @click="next">Continue</v-btn>
                    <v-btn v-else depressed color="#2a7f86" class="acd-btn white--text ml-3" @click="save">Create
                        company</v-btn>
                </template>
                <!-- โหมด edit: กด Save changes ได้ทุกขั้น (Continue ไว้ไปขั้นถัดไปเฉย ๆ) -->
                <template v-else>
                    <v-btn v-if="step < 4" outlined color="#2a7f86" class="acd-btn ml-3" @click="next">Continue</v-btn>
                    <v-btn depressed color="#2a7f86" class="acd-btn white--text ml-3" @click="save">Save changes</v-btn>
                </template>
            </div>

            <!-- ยืนยันก่อนลบเมนูออกจากบริษัท (โหมด edit) -->
            <v-dialog v-model="confirmRemove" max-width="440px" persistent>
                <v-card class="acd">
                    <div class="acd-header">
                        <span class="acd-title">Remove menus?</span>
                    </div>
                    <v-card-text class="acd-body">
                        {{ removeSummary.details }} submenu(s)<span v-if="removeSummary.groups"> in
                            {{ removeSummary.groups }} group(s)</span> will be removed from this company, together with
                        their permissions for every role. This cannot be undone.
                    </v-card-text>
                    <div class="acd-footer">
                        <v-spacer></v-spacer>
                        <v-btn outlined class="acd-btn" @click="answerConfirm(false)">Cancel</v-btn>
                        <v-btn depressed color="#c62828" class="acd-btn white--text ml-3"
                            @click="answerConfirm(true)">Remove and
                            save</v-btn>
                    </div>
                </v-card>
            </v-dialog>
        </v-card>
    </v-dialog>
</template>

<script>
import api from "@/services/api";
import { server } from "@/services/constants";
import {
    getRegisterCompanyMasterTemplate,
    updateRegisterCompany, // ⭐ ใหม่ (หลังบ้าน): บันทึก edit ทั้งหมดใน transaction เดียว
} from "@/services/api-dtime/api_register_company";

// แปลง response ได้ทั้ง [...] / { data: [...] } / { rows: [...] }
const toArray = (res) => {
    const d = res && res.data !== undefined ? res.data : res;
    if (Array.isArray(d)) return d;
    if (d && Array.isArray(d.data)) return d.data;
    if (d && Array.isArray(d.rows)) return d.rows;
    return [];
};

// จับคู่เมนูของบริษัทกับเมนูต้นแบบ (ต้องตรงกับฝั่ง backend: syncMenuTemplateForCompany)
// group: ชื่อเหมือนกัน, submenu: route เหมือนกัน หรือชื่อเหมือนกัน
const norm = (v) => (v || "").toString().trim().toLowerCase();
const normRoute = (v) => norm(v).replace(/^\/+/, "");
const sameGroup = (a, b) => norm(a.cgm_name) === norm(b.cgm_name);
const sameDetail = (a, b) =>
    (normRoute(a.cmd_route) !== "" && normRoute(a.cmd_route) === normRoute(b.cmd_route)) ||
    norm(a.cmd_name) === norm(b.cmd_name);

const emptyUser = () => ({
    Firstname: "",
    Lastname: "",
    Email: "",
    User_Telephone_Number: "",
    Username: "",
    Password: "",
    Confirm_Password: "",
});

const emptyCompany = () => ({
    code: "",
    name: "",
    company_address: "",
    company_address2: "",
    subdistrict: "",
    district: "",
    province: "",
    postcode: "",
    country: "",
    company_branch: "",
    company_email: "",
    company_tel: "",
    inv_company_name: "",
    inv_company_name_th: "",
    inv_company_address: "",
    inv_company_address2: "",
    inv_subdistrict: "",
    inv_district: "",
    inv_province: "",
    inv_postcode: "",
    inv_country: "",
    inv_company_branch: "",
    inv_company_email: "",
    inv_company_tel: "",
    inv_tax_id: "",
    status: "ACTIVE",
    module: "Employee",
    id_prefix: "",
    running_year: new Date().getFullYear().toString().slice(-2),
    running_len: 1,
    running_next: 1,
    doc_status: 0,
});

// รายชื่อจังหวัด (ใช้ใน dropdown Province, พิมพ์ค่าอื่นเองได้)
const PROVINCES = [
    "Bangkok", "Amnat Charoen", "Ang Thong", "Bueng Kan", "Buriram", "Chachoengsao", "Chai Nat",
    "Chaiyaphum", "Chanthaburi", "Chiang Mai", "Chiang Rai", "Chonburi", "Chumphon", "Kalasin",
    "Kamphaeng Phet", "Kanchanaburi", "Khon Kaen", "Krabi", "Lampang", "Lamphun", "Loei", "Lopburi",
    "Mae Hong Son", "Maha Sarakham", "Mukdahan", "Nakhon Nayok", "Nakhon Pathom", "Nakhon Phanom",
    "Nakhon Ratchasima", "Nakhon Sawan", "Nakhon Si Thammarat", "Nan", "Narathiwat", "Nong Bua Lamphu",
    "Nong Khai", "Nonthaburi", "Pathum Thani", "Pattani", "Phang Nga", "Phatthalung", "Phayao",
    "Phetchabun", "Phetchaburi", "Phichit", "Phitsanulok", "Phra Nakhon Si Ayutthaya", "Phrae", "Phuket",
    "Prachinburi", "Prachuap Khiri Khan", "Ranong", "Ratchaburi", "Rayong", "Roi Et", "Sa Kaeo",
    "Sakon Nakhon", "Samut Prakan", "Samut Sakhon", "Samut Songkhram", "Saraburi", "Satun", "Sing Buri",
    "Sisaket", "Songkhla", "Sukhothai", "Suphan Buri", "Surat Thani", "Surin", "Tak", "Trang", "Trat",
    "Ubon Ratchathani", "Udon Thani", "Uthai Thani", "Uttaradit", "Yala", "Yasothon",
];

const COUNTRIES = [
    "Thailand", "Cambodia", "China", "Hong Kong", "India", "Indonesia", "Japan", "Laos", "Malaysia",
    "Myanmar", "Philippines", "Singapore", "South Korea", "Taiwan", "Vietnam", "Australia",
    "United Kingdom", "United States",
];

// ฟิลด์ invoice ที่ถูก copy มาจากข้อมูลบริษัทเมื่อติ๊ก "Same as company information"
const COPIED_INVOICE_REFS = [
    "INV_Company_Name_Add",
    "Invoice_Address_Add",
    "inv_subdistrict",
    "inv_district",
    "inv_province",
    "inv_postcode",
    "inv_country",
    "INV_Branch_Add",
    "INV_Email_Add",
    "INV_Telephone_Number_Add",
];

export default {
    name: "AddCompanyDialog",
    props: {
        value: { type: Boolean, default: false },
        disabledStatus: { type: Boolean, default: false },
        // "add" = สร้างบริษัทใหม่, "edit" = แก้บริษัทที่มีอยู่ (ส่ง company = แถวจากตาราง)
        mode: { type: String, default: "add" },
        company: { type: Object, default: null },
    },
    data: () => ({
        step: 1,
        stepLabels: ["User information", "Company information", "Billing", "Menu access"],
        user: emptyUser(),
        com: emptyCompany(),
        sameAs: false,
        showPass: false,
        showConfirm: false,

        // Menu access
        groups: [],
        details: [],
        activeGroupId: null,
        selectedLeafGroupIds: [], // กลุ่มที่ไม่มี submenu (เช่น Dashboard ที่มี route ตรง)
        selectedDetailIds: [],
        menuSearch: "",
        loadingMenus: false,

        // โหมด edit: เมนูที่บริษัทมีอยู่ตอนเปิด (ไว้คำนวณว่าจะลบอะไร)
        initialDetailIds: [],
        initialLeafGroupIds: [],
        confirmRemove: false,
        removeSummary: { groups: 0, details: 0 },
        confirmResolver: null,

        statusall: ["ACTIVE", "INACTIVE"],
        provinceList: PROVINCES,
        countryList: COUNTRIES,
        emp_assign: [
            { name: "Manual", values: 1 },
            { name: "Auto-generated", values: 0 },
        ],
        emailRules: [
            (v) =>
                !v ||
                /^(([^<>()[\]\\.,;:\s@']+(\.[^<>()\\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v) ||
                "Please enter a valid email address",
        ],
        telRules: [(v) => !v || /^[+]?([0-9,-]+)$/.test(v) || "Please Input Number Only"],
        comcodeRules: [(v) => (!v || v.length > 6 ? "Please check that the Company Code" : true)],
        phoneRules: [
            (v) => !!v || "Phone number is required",
            (v) => /^[0-9]*$/.test(v) || "Numbers only",
            (v) => (v && v.length <= 10) || "Max 10 digits",
        ],
    }),
    computed: {
        isEdit() {
            return this.mode === "edit";
        },
        companyId() {
            return this.company ? Number(this.company.id) : null;
        },
        firstStep() {
            return this.isEdit ? 2 : 1; // โหมด edit ไม่มีขั้น User information
        },
        stepItems() {
            return this.stepLabels
                .map((label, i) => ({ n: i + 1, label }))
                .filter((s) => s.n >= this.firstStep);
        },
        stepPosition() {
            return this.stepItems.findIndex((s) => s.n === this.step) + 1;
        },
        // ข้อความยาวเก็บไว้ใน computed กัน formatter ตัดสตริงใน template ขาดบรรทัด
        codeHint() {
            return this.isEdit ? "Company code cannot be changed" : "Maximum 6 characters";
        },
        menuSubtitle() {
            return this.isEdit
                ? "Choose which group menus and submenus this company can access. Unticked menus are removed when you save."
                : "Choose which group menus and submenus this account can access.";
        },
        footerHint() {
            if (this.step !== 4) return `Step ${this.stepPosition} of ${this.stepItems.length}`;
            return this.isEdit
                ? "Unticked menus are removed from this company when you save."
                : "Changes are saved when the company is created.";
        },
        isDev() {
            return process.env.NODE_ENV === "development";
        },
        companyAddressLine() {
            const c = this.com;
            return [c.company_address, c.company_address2, c.subdistrict, c.district, c.province, c.postcode, c.country]
                .map((s) => (s || "").trim())
                .filter(Boolean)
                .join(", ");
        },
        // ⚠️ รูปแบบนี้เป็นตัวอย่างสำหรับแสดงผลเท่านั้น ให้ปรับให้ตรงกับรูปแบบที่ backend สร้างจริง
        employeeIdPreview() {
            const len = Math.max(parseInt(this.com.running_len, 10) || 1, 1);
            const next = Math.max(parseInt(this.com.running_next, 10) || 1, 1);
            const num = String(next).padStart(len, "0");
            const prefix = (this.com.id_prefix || "").trim();
            return prefix ? `${prefix}-${num}` : num;
        },
        detailsByGroup() {
            const map = {};
            this.details.forEach((d) => {
                (map[d.group_menu_id] = map[d.group_menu_id] || []).push(d);
            });
            return map;
        },
        searchText() {
            return (this.menuSearch || "").trim().toLowerCase();
        },
        filteredGroups() {
            if (!this.searchText) return this.groups;
            return this.groups.filter(
                (g) =>
                    (g.cgm_name || "").toLowerCase().includes(this.searchText) ||
                    this.subsOf(g).some((d) => (d.cmd_name || "").toLowerCase().includes(this.searchText)),
            );
        },
        activeGroup() {
            return this.groups.find((g) => g.id === this.activeGroupId) || null;
        },
        activeDetails() {
            if (!this.activeGroup) return [];
            const subs = this.subsOf(this.activeGroup);
            const groupMatches = (this.activeGroup.cgm_name || "").toLowerCase().includes(this.searchText);
            if (!this.searchText || groupMatches) return subs;
            return subs.filter((d) => (d.cmd_name || "").toLowerCase().includes(this.searchText));
        },
        selectedGroupCount() {
            return this.groups.filter((g) => this.groupState(g) !== "none").length;
        },
        allMenusSelected: {
            get() {
                return this.groups.length > 0 && this.groups.every((g) => this.groupState(g) === "all");
            },
            set(val) {
                if (val) {
                    this.selectedDetailIds = this.details.map((d) => d.id);
                    this.selectedLeafGroupIds = this.groups.filter((g) => this.subsOf(g).length === 0).map((g) => g.id);
                } else {
                    this.selectedDetailIds = [];
                    this.selectedLeafGroupIds = [];
                }
            },
        },
    },
    watch: {
        value(open) {
            if (open) this.reset();
        },
        sameAs(val) {
            if (val) this.copyCompanyToInvoice();
        },
        filteredGroups(list) {
            if (list.length && !list.some((g) => g.id === this.activeGroupId)) {
                this.activeGroupId = list[0].id;
            }
        },
    },
    methods: {
        isNumber(evt) {
            const charCode = evt.which ? evt.which : evt.keyCode;
            if (charCode > 31 && (charCode < 48 || charCode > 57)) evt.preventDefault();
        },

        async reset() {
            this.step = this.firstStep;
            this.user = emptyUser();
            this.com = emptyCompany();
            this.sameAs = false;
            this.showPass = false;
            this.showConfirm = false;
            this.menuSearch = "";
            this.initialDetailIds = [];
            this.initialLeafGroupIds = [];
            if (this.groups.length === 0) await this.loadMenus();

            if (this.isEdit) {
                await this.loadCompanyForEdit();
            } else {
                this.allMenusSelected = true; // เริ่มต้นเลือกทุกเมนู
            }
            this.activeGroupId = this.groups.length ? this.groups[0].id : null;
        },

        // ---------------- Edit mode ----------------
        async loadCompanyForEdit() {
            const it = this.company || {};
            const com = emptyCompany();
            Object.keys(com).forEach((k) => {
                if (it[k] !== undefined && it[k] !== null) com[k] = it[k];
            });
            this.com = com;

            this.$showLoader();
            try {
                // Employee ID settings
                try {
                    const resDoc = await api.fineDocRunningByModlue("Employee", this.companyId);
                    const d = resDoc && resDoc.data;
                    if (d) {
                        Object.assign(this.com, {
                            id_prefix: d.id_prefix || "",
                            running_year: d.running_year,
                            running_len: d.running_len,
                            running_next: d.running_next,
                            doc_status: d.check_doc_auto,
                        });
                    }
                } catch (error) {
                    console.error("AddCompanyDialog load doc running error:", error);
                }

                // เมนูที่บริษัทมีอยู่ -> ติ๊กให้ตรงกับเมนูต้นแบบ
                await this.loadCompanyMenuSelection();
            } finally {
                this.$hideLoader();
            }
        },

        async loadCompanyMenuSelection() {
            let cGroups = [];
            let cDetails = [];
            try {
                const [rg, rd] = await Promise.all([
                    api.getGroupMenu_By_CompanyID(this.companyId),
                    api.getAllDetailGroupMenu_By_CompanyID(this.companyId),
                ]);
                cGroups = toArray(rg);
                cDetails = toArray(rd);
            } catch (error) {
                console.error("AddCompanyDialog load company menus error:", error);
            }

            const detailIds = [];
            const leafIds = [];
            this.groups.forEach((mg) => {
                const cg = cGroups.find((g) => sameGroup(g, mg));
                if (!cg) return;
                const subs = this.subsOf(mg);
                if (subs.length === 0) {
                    leafIds.push(mg.id);
                    return;
                }
                const cSubs = cDetails.filter((d) => Number(d.group_menu_id) === Number(cg.id));
                subs.forEach((md) => {
                    if (cSubs.some((cd) => sameDetail(cd, md))) detailIds.push(md.id);
                });
            });

            this.selectedDetailIds = detailIds;
            this.selectedLeafGroupIds = leafIds;
            this.initialDetailIds = [...detailIds];
            this.initialLeafGroupIds = [...leafIds];
        },

        // กันซ้ำ: ในโหมด edit ไม่นับแถวของบริษัทตัวเอง
        othersOnly(rows) {
            const list = Array.isArray(rows) ? rows : [];
            return this.isEdit ? list.filter((r) => Number(r.id) !== this.companyId) : list;
        },

        countRemovedMenus() {
            const removedDetails = this.initialDetailIds.filter((id) => !this.selectedDetailIds.includes(id));
            const removedLeaf = this.initialLeafGroupIds.filter((id) => !this.selectedLeafGroupIds.includes(id));
            const hadBefore = (g) =>
                this.subsOf(g).some((d) => this.initialDetailIds.includes(d.id)) ||
                this.initialLeafGroupIds.includes(g.id);
            const removedGroups = this.groups.filter((g) => hadBefore(g) && this.groupState(g) === "none");
            return { details: removedDetails.length + removedLeaf.length, groups: removedGroups.length };
        },

        askConfirmRemove(summary) {
            this.removeSummary = summary;
            this.confirmRemove = true;
            return new Promise((resolve) => {
                this.confirmResolver = resolve;
            });
        },

        answerConfirm(ok) {
            this.confirmRemove = false;
            if (this.confirmResolver) this.confirmResolver(ok);
            this.confirmResolver = null;
        },

        async saveEdit() {
            this.$showLoader();
            try {
                for (const s of [2, 3]) {
                    if (!(await this.validateStep(s))) return;
                }
                if (this.sameAs) this.copyCompanyToInvoice();

                const groupMenuIds = this.groups.filter((g) => this.groupState(g) !== "none").map((g) => g.id);
                if (groupMenuIds.length === 0) {
                    this.fail("Please select at least one menu", null);
                    return;
                }

                const removed = this.countRemovedMenus();
                if (removed.details + removed.groups > 0) {
                    this.$hideLoader();
                    const ok = await this.askConfirmRemove(removed);
                    if (!ok) return;
                    this.$showLoader();
                }

                if (typeof updateRegisterCompany !== "function") {
                    throw new Error("updateRegisterCompany is not defined in api_register_company.js");
                }

                // บันทึกทั้งหมด (company, employee id, เมนู) ผ่าน API edit ตัวเดียว
                // หลังบ้านทำใน transaction เดียว ถ้าพังจะ rollback ทั้งหมด (โหมด edit ไม่ส่งข้อมูล user)
                const item_data = {
                    ...this.buildPayload(groupMenuIds),
                    status: this.com.status,
                    user_update: localStorage.getItem(server.USER_ID),
                };

                const res = await updateRegisterCompany(this.companyId, item_data);
                if (!(res.status == 200 || res.status == 201)) {
                    throw new Error(`updateRegisterCompany status ${res.status}`);
                }

                this.$emit("alert", {
                    title: "Success",
                    message: "Update data success",
                    color: "text-h5 green--text text-center",
                });
                this.$emit("saved");
                this.close();
            } catch (error) {
                console.error("AddCompanyDialog saveEdit error:", error);
                this.$emit("alert", {
                    title: "Failed",
                    message: "Update data Failed",
                    color: "text-h5 red--text text-center",
                });
            } finally {
                this.$hideLoader();
            }
        },

        async loadMenus() {
            this.loadingMenus = true;

            // 1) กลุ่มเมนูต้นแบบ
            try {
                const resGroups = await getRegisterCompanyMasterTemplate();
                console.log("AddCompanyDialog master template:", resGroups && resGroups.data);
                this.groups = toArray(resGroups).sort(
                    (a, b) => (a.cgm_sort_order || 0) - (b.cgm_sort_order || 0),
                );
            } catch (error) {
                console.error("AddCompanyDialog load group menus error:", error);
                this.groups = [];
            }

            // 2) submenu ของบริษัทต้นแบบ (แยก try ไว้ ถ้าพังกลุ่มเมนูยังแสดงได้)
            try {
                const masterCompanyId = (this.groups[0] && this.groups[0].company_id) || 1;
                const resDetails = await api.getAllDetailGroupMenu_By_CompanyID(masterCompanyId);
                const groupIds = new Set(this.groups.map((g) => g.id));
                this.details = toArray(resDetails)
                    .filter((d) => groupIds.has(d.group_menu_id))
                    .sort((a, b) => (a.cmd_sort_order || 0) - (b.cmd_sort_order || 0));
            } catch (error) {
                console.error("AddCompanyDialog load submenus error:", error);
                this.details = [];
            } finally {
                this.loadingMenus = false;
            }
        },

        // ---------------- Test data (dev only) ----------------
        // กรอกข้อมูลตัวอย่างทุกขั้น สุ่มเลขท้ายให้ email / เบอร์ / รหัสบริษัท ไม่ซ้ำกันทุกครั้งที่กด
        fillTestData() {
            const rand = String(Math.floor(Math.random() * 90000) + 10000); // 5 หลัก

            // Email และเบอร์โทร ไม่กรอกให้ (ใส่เอง) — ค่าที่พิมพ์ไว้แล้วจะไม่ถูกทับ
            this.user = {
                ...this.user,
                Firstname: "Test",
                Lastname: `User${rand}`,
                Username: `testuser${rand}`,
                Password: "Test@1234",
                Confirm_Password: "Test@1234",
            };

            this.com = {
                ...emptyCompany(),
                code: `T${rand}`, // 6 ตัวอักษร
                name: `Test Company ${rand} Co., Ltd.`,
                company_address: "123 Sukhumvit Road",
                company_address2: "5th Floor, Test Tower",
                subdistrict: "Khlong Tan",
                district: "Khlong Toei",
                province: "Bangkok",
                postcode: "10110",
                country: "Thailand",
                company_branch: "Head Office",
                company_email: `company${rand}@example.com`,
                company_tel: "021234567",
                inv_company_name_th: `บริษัท ทดสอบ ${rand} จำกัด`,
                inv_tax_id: `0105${rand}1234`.slice(0, 13),
                id_prefix: "EMP",
                running_len: 6,
                running_next: 1,
                doc_status: 0,
            };

            // invoice ใช้ข้อมูลเดียวกับบริษัท (ติ๊ก Same as company information ให้)
            this.sameAs = false;
            this.$nextTick(() => {
                this.sameAs = true;
            });
        },

        // ---------------- Menu access helpers ----------------
        subsOf(g) {
            return this.detailsByGroup[g.id] || [];
        },
        groupTotalCount(g) {
            const subs = this.subsOf(g);
            return subs.length || 1;
        },
        groupSelectedCount(g) {
            const subs = this.subsOf(g);
            if (subs.length === 0) return this.selectedLeafGroupIds.includes(g.id) ? 1 : 0;
            return subs.filter((d) => this.selectedDetailIds.includes(d.id)).length;
        },
        groupState(g) {
            const selected = this.groupSelectedCount(g);
            if (selected === 0) return "none";
            return selected === this.groupTotalCount(g) ? "all" : "some";
        },
        toggleGroup(g) {
            const subs = this.subsOf(g);
            if (subs.length === 0) {
                this.selectedLeafGroupIds = this.selectedLeafGroupIds.includes(g.id)
                    ? this.selectedLeafGroupIds.filter((id) => id !== g.id)
                    : [...this.selectedLeafGroupIds, g.id];
                return;
            }
            const ids = subs.map((d) => d.id);
            if (this.groupState(g) === "all") {
                this.selectedDetailIds = this.selectedDetailIds.filter((id) => !ids.includes(id));
            } else {
                this.selectedDetailIds = Array.from(new Set([...this.selectedDetailIds, ...ids]));
            }
        },
        toggleDetail(id) {
            this.selectedDetailIds = this.selectedDetailIds.includes(id)
                ? this.selectedDetailIds.filter((x) => x !== id)
                : [...this.selectedDetailIds, id];
        },

        // ---------------- Invoice ----------------
        copyCompanyToInvoice() {
            const c = this.com;
            c.inv_company_name = c.name.trim();
            c.inv_company_address = c.company_address.trim();
            c.inv_company_address2 = c.company_address2.trim();
            c.inv_subdistrict = c.subdistrict.trim();
            c.inv_district = c.district.trim();
            c.inv_province = c.province.trim();
            c.inv_postcode = c.postcode.trim();
            c.inv_country = c.country.trim();
            c.inv_company_branch = c.company_branch.trim();
            c.inv_company_email = c.company_email.trim();
            c.inv_company_tel = c.company_tel.trim();
        },

        // ---------------- Validation ----------------
        checksForStep(step) {
            const emailRegex = /^(([^<>()[\]\\.,;:\s@']+(\.[^<>()\\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            const telRegex = /^[+]?([0-9,-]+)$/;
            const passRegex = /^(?=.*[0-9])(?=.*[^A-Za-z0-9]).{8,}$/;
            const u = this.user;
            const c = this.com;
            const t = (v) => (v || "").toString().trim();

            if (step === 1) {
                return [
                    { test: () => t(u.Firstname) !== "", ref: "Firstname", msg: "Please Check Firstname Data" },
                    { test: () => t(u.Lastname) !== "", ref: "Lastname", msg: "Please Check Lastname Data" },
                    { test: () => emailRegex.test(t(u.Email)), ref: "Email", msg: "Please Check Email Data" },
                    {
                        test: async () => (await api.getUserByEmail(t(u.Email))).data.length === 0,
                        ref: "Email",
                        msg: "Email Duplicated, Please Check Email",
                    },
                    { test: () => t(u.User_Telephone_Number) !== "", ref: "user_phone", msg: "กรุณากรอกเบอร์โทรศัพท์" },
                    {
                        test: async () =>
                            (await api.getUserByPhoneComID(u.User_Telephone_Number, localStorage.getItem(server.COMPANYID))).data
                                .length === 0,
                        ref: "user_phone",
                        msg: "เบอร์โทรศัพท์นี้ถูกใช้งานแล้ว กรุณาตรวจสอบเบอร์โทรศัพท์",
                    },
                    { test: () => t(u.Username) !== "", ref: "Username", msg: "Please Check Username Data" },
                    { test: () => t(u.Password) !== "", ref: "Password", msg: "Please Check Password Data" },
                    {
                        test: () => passRegex.test(t(u.Password)),
                        ref: "Password",
                        msg: "Password must be at least 8 characters with a number and symbol",
                    },
                    { test: () => t(u.Confirm_Password) !== "", ref: "Confirm_Password", msg: "Please Check Confirm Password Data" },
                    {
                        test: () => t(u.Password) === t(u.Confirm_Password),
                        ref: "Confirm_Password",
                        msg: "Please Check Password and Confirm Password",
                    },
                ];
            }

            if (step === 2) {
                // บังคับกรอกตามดีไซน์: Company name, Company code, Address line 1, Country
                // ช่องอื่นไม่บังคับ แต่ถ้ากรอก email / telephone ต้องรูปแบบถูกต้อง
                return [
                    { test: () => t(c.name) !== "", ref: "Company_Name_Add", msg: "Please Check Company Name Data" },
                    {
                        // โหมด edit แก้รหัสบริษัทไม่ได้ ไม่ต้องตรวจ
                        test: () => this.isEdit || (t(c.code) !== "" && t(c.code).length <= 6),
                        ref: "Company_Code_Add",
                        msg: "Please Check Company Code Data",
                    },
                    {
                        test: async () => this.isEdit || (await api.getCompanyByComCode(t(c.code))).data.length === 0,
                        ref: "Company_Code_Add",
                        msg: "Company Code Duplicated, Please Check Company Code",
                    },
                    { test: () => t(c.company_address) !== "", ref: "Company_Address_Add", msg: "Please check the company information again." },
                    { test: () => t(c.country) !== "", ref: "country", msg: "Please select a country" },
                    {
                        test: () => !t(c.company_email) || emailRegex.test(t(c.company_email)),
                        ref: "Company_Email_Add",
                        msg: "Please Check Company Email Data",
                    },
                    {
                        test: async () =>
                            !t(c.company_email) ||
                            this.othersOnly((await api.getCompanyCol(t(c.company_email), "company_email")).data).length === 0,
                        ref: "Company_Email_Add",
                        msg: "Email Duplicated, Please Check Email",
                    },
                    {
                        test: () => !t(c.company_tel) || telRegex.test(t(c.company_tel)),
                        ref: "Company_Telephone_Number_Add",
                        msg: "Please Check Telephone Number Data",
                    },
                ];
            }

            if (step === 3) {
                // บังคับกรอกตามดีไซน์: Company name, Invoice address line 1, Country, Tax ID
                // ช่องอื่นไม่บังคับ แต่ถ้ากรอก email / telephone ต้องรูปแบบถูกต้อง
                const checks = [
                    { test: () => t(c.inv_company_name) !== "", ref: "INV_Company_Name_Add", msg: "Please Check Company Name Data" },
                    { test: () => t(c.inv_company_address) !== "", ref: "Invoice_Address_Add", msg: "Please check the invoice information again." },
                    { test: () => t(c.inv_country) !== "", ref: "inv_country", msg: "Please check the invoice information again." },
                    {
                        test: () => !t(c.inv_company_email) || emailRegex.test(t(c.inv_company_email)),
                        ref: "INV_Email_Add",
                        msg: "Please Check Company Email Data",
                    },
                    {
                        test: async () =>
                            !t(c.inv_company_email) ||
                            this.othersOnly((await api.getCompanyCol(t(c.inv_company_email), "inv_company_email")).data).length === 0,
                        ref: "INV_Email_Add",
                        msg: "Email Duplicated, Please Check Email",
                    },
                    {
                        test: () => !t(c.inv_company_tel) || telRegex.test(t(c.inv_company_tel)),
                        ref: "INV_Telephone_Number_Add",
                        msg: "Please Check Telephone Number Data",
                    },
                    { test: () => t(c.inv_tax_id) !== "", ref: "INV_Tax_ID_Add", msg: "Please Check Tax ID Data" },
                ];
                if (c.doc_status == 0) {
                    checks.push(
                        { test: () => Number(c.running_len) >= 1, ref: "Range", msg: "Please Check Range Data" },
                        { test: () => Number(c.running_next) >= 1, ref: "Next Running", msg: "Please Check Next Running Data" },
                    );
                }
                return checks;
            }

            return [];
        },

        async validateStep(step) {
            if (step === 3 && this.sameAs) this.copyCompanyToInvoice();

            for (const check of this.checksForStep(step)) {
                if (!(await check.test())) {
                    this.step = step;
                    if (step === 3 && this.sameAs && COPIED_INVOICE_REFS.includes(check.ref)) {
                        this.sameAs = false; // เปิดฟอร์ม invoice เต็มเพื่อให้แก้ช่องที่ผิดได้
                    }
                    this.fail(check.msg, check.ref);
                    return false;
                }
            }
            return true;
        },

        fail(msg, ref) {
            this.$emit("alert", { title: "Failed", message: msg, color: "text-h5 red--text text-center" });
            this.$nextTick(() => {
                const el = this.$refs[ref];
                if (el && el.focus) el.focus();
            });
        },

        async next() {
            this.$showLoader();
            try {
                if (await this.validateStep(this.step)) this.step++;
            } finally {
                this.$hideLoader();
            }
        },

        close() {
            this.$emit("input", false);
        },

        // ---------------- Save ----------------
        // ข้อมูลที่ส่งเหมือนกันทั้ง add และ edit (ไม่มีข้อมูล user / company code)
        buildPayload(groupMenuIds) {
            const c = this.com;
            const t = (v) => (v || "").toString().trim();
            return {
                // company
                Company_Name: t(c.name),
                Company_Address: t(c.company_address),
                Company_Branch: t(c.company_branch),
                Company_Email: t(c.company_email),
                Company_Telephone_Number: t(c.company_tel),
                // invoice
                INV_Company_Name: t(c.inv_company_name),
                INV_Company_Name_TH: t(c.inv_company_name_th),
                Invoice_Address: t(c.inv_company_address),
                INV_Branch: t(c.inv_company_branch),
                INV_Email: t(c.inv_company_email),
                INV_Telephone_Number: t(c.inv_company_tel),
                INV_Tax_ID: t(c.inv_tax_id),
                // employee id
                module: "Employee",
                id_prefix: c.id_prefix,
                running_year: c.running_year,
                running_len: c.running_len,
                running_next: c.running_next,
                doc_status: c.doc_status,
                // address details
                company_address2: c.company_address2,
                subdistrict: c.subdistrict,
                district: c.district,
                province: c.province,
                postcode: c.postcode,
                country: c.country,
                inv_company_address2: c.inv_company_address2,
                inv_subdistrict: c.inv_subdistrict,
                inv_district: c.inv_district,
                inv_province: c.inv_province,
                inv_postcode: c.inv_postcode,
                inv_country: c.inv_country,
                // menu access
                group_menu_ids: JSON.stringify(groupMenuIds),
                menu_detail_ids: JSON.stringify(this.selectedDetailIds),
            };
        },

        async save() {
            if (this.isEdit) return this.saveEdit();
            this.$showLoader();
            try {
                for (const s of [1, 2, 3]) {
                    if (!(await this.validateStep(s))) return;
                }

                const groupMenuIds = this.groups.filter((g) => this.groupState(g) !== "none").map((g) => g.id);
                if (groupMenuIds.length === 0) {
                    this.fail("Please select at least one menu", null);
                    return;
                }

                const u = this.user;
                const t = (v) => (v || "").toString().trim();
                const item_data = {
                    // user (เฉพาะตอน add)
                    Firstname: t(u.Firstname),
                    Lastname: t(u.Lastname),
                    User_Telephone_Number: t(u.User_Telephone_Number),
                    Email: t(u.Email),
                    Username: t(u.Username),
                    Password: t(u.Password),
                    Confirm_Password: t(u.Confirm_Password),
                    Company_Code: t(this.com.code),
                    ...this.buildPayload(groupMenuIds),
                };

                const res = await api.addRegsiterCompany(item_data);
                if (res.status == 201) {
                    this.$emit("alert", {
                        title: "Information Saved",
                        message: "Information successfully saved",
                        color: "text-h5 green--text text-center",
                    });
                    this.$emit("saved");
                    this.close();
                } else {
                    this.$emit("alert", {
                        title: "Information Saved",
                        message: "Information Save failed",
                        color: "text-h5 red--text text-center",
                    });
                }
            } catch (error) {
                console.error("AddCompanyDialog save error:", error);
                this.$emit("alert", {
                    title: "Failed",
                    message: "Something went wrong",
                    color: "text-h5 red--text text-center",
                });
            } finally {
                this.$hideLoader();
            }
        },
    },
};
</script>

<style scoped>
.acd {
    border-radius: 14px !important;
    color: #16253d;
}

.acd-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 22px 12px;
    border-bottom: 1px solid #e5e7eb;
}

.acd-title {
    font-size: 1.2rem;
    font-weight: 700;
}

/* Stepper */
.acd-stepper {
    display: flex;
    align-items: center;
    padding: 12px 22px;
    border-bottom: 1px solid #f1f1f1;
}

.acd-step {
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.acd-step.clickable {
    cursor: pointer;
}

.acd-step-circle {
    width: 28px;
    height: 28px;
    font-size: 0.8rem;
    border-radius: 50%;
    background: #9ca3af;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
}

.acd-step-circle.active,
.acd-step-circle.done {
    background: #2a7f86;
}

.acd-step-label {
    margin-left: 8px;
    font-size: 0.85rem;
    color: #4b5563;
    white-space: nowrap;
}

.acd-step-label.active {
    color: #2a7f86;
    font-weight: 600;
}

.acd-step-line {
    flex: 1;
    height: 2px;
    background: #d1d5db;
    margin: 0 12px;
    min-width: 16px;
}

.acd-step-line.done {
    background: #2a7f86;
}

/* Body */
.acd-body {
    padding: 16px 22px !important;
    font-size: 0.875rem;
    color: #16253d !important;
}

.acd-h2 {
    font-size: 1.1rem;
    font-weight: 700;
    margin-bottom: 4px;
}

.acd-sub {
    color: #6b7280;
    font-size: 0.85rem;
    margin-bottom: 12px;
}

.acd-field label {
    display: block;
    font-size: 0.8rem;
    color: #16253d;
    margin-bottom: 4px;
}

.req {
    color: #e53935;
}

.acd-section-title {
    font-size: 0.95rem;
    font-weight: 700;
    margin-bottom: 6px;
}

.acd-counter {
    font-size: 0.75rem;
    color: #6b7280;
}

.acd-hint {
    font-size: 0.75rem;
    color: #6b7280;
}

.acd-field .acd-hint {
    margin-top: 6px;
}

.acd-info {
    display: flex;
    align-items: center;
    width: 100%;
    background: #f1f4f8;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 0.8rem;
    color: #374151;
}

.acd-panel {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 14px 16px;
    height: 100%;
}

.acd-invoice-scroll {
    max-height: 380px;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 8px;
}

.acd-invoice-scroll::-webkit-scrollbar {
    width: 6px;
}

.acd-invoice-scroll::-webkit-scrollbar-thumb {
    background: #c4c9d0;
    border-radius: 6px;
}

.acd-panel-title {
    font-size: 0.95rem;
    font-weight: 700;
    margin-bottom: 10px;
}

.acd-summary {
    background: #f1f4f8;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 0.8rem;
    line-height: 1.6;
    margin-bottom: 10px;
}

.acd-link {
    color: #2a7f86 !important;
    text-decoration: underline;
    cursor: pointer;
}

.acd-preview {
    background: #f1f4f8;
    border-radius: 8px;
    padding: 10px 14px;
}

.acd-preview-id {
    font-size: 1.2rem;
    font-weight: 700;
    letter-spacing: 0.5px;
}

/* Menu access */
.acd-badge {
    background: #e0f2f1;
    color: #2a7f86;
    font-weight: 600;
    border-radius: 8px;
    font-size: 0.8rem;
    padding: 6px 12px;
}

.acd-menu {
    display: flex;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
    min-height: 300px;
}

.acd-menu-left {
    width: 45%;
    border-right: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
}

.acd-menu-right {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.acd-menu-head {
    background: #f8fafc;
    padding: 10px 16px;
    font-size: 0.85rem;
    font-weight: 700;
    border-bottom: 1px solid #e5e7eb;
}

.acd-menu-scroll {
    flex: 1;
    overflow-y: auto;
    max-height: 300px;
}

.acd-group-row {
    display: flex;
    align-items: center;
    padding: 8px 16px;
    border-bottom: 1px solid #f1f1f1;
    cursor: pointer;
}

.acd-group-row:hover {
    background: #f8fafc;
}

.acd-group-row.active {
    background: #e6f2f2;
}

.acd-group-name {
    flex: 1;
    margin-left: 8px;
}

.acd-count {
    color: #4b5563;
    font-size: 0.75rem;
}

.acd-suball {
    display: flex;
    align-items: center;
    padding: 8px 16px;
    border-bottom: 1px solid #e5e7eb;
}

.acd-subgrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
}

.acd-subitem {
    display: flex;
    align-items: center;
    padding: 8px 16px;
    border-bottom: 1px solid #f1f1f1;
    cursor: pointer;
}

.acd-subitem:nth-child(odd) {
    border-right: 1px solid #f1f1f1;
}

.acd-menu-foot {
    padding: 8px 16px;
    font-size: 0.75rem;
    color: #6b7280;
    border-top: 1px solid #e5e7eb;
}

/* Footer */
.acd-footer {
    display: flex;
    align-items: center;
    padding: 12px 22px;
    border-top: 1px solid #e5e7eb;
}

.acd-btn {
    text-transform: none !important;
    font-weight: 600;
    letter-spacing: normal;
    height: 36px !important;
    min-width: 90px !important;
    font-size: 0.85rem;
    border-radius: 8px;
}

/* ย่อ input / checkbox ของ Vuetify ภายใน dialog */
.acd ::v-deep .v-text-field input,
.acd ::v-deep .v-select__selection {
    font-size: 0.85rem;
}

.acd ::v-deep .v-input--dense>.v-input__control>.v-input__slot {
    min-height: 36px !important;
}

.acd ::v-deep .v-input--checkbox .v-label {
    font-size: 0.85rem;
}

.acd ::v-deep .v-messages {
    font-size: 0.7rem;
}

@media (max-width: 760px) {
    .acd-step-label {
        display: none;
    }

    .acd-menu {
        flex-direction: column;
    }

    .acd-menu-left {
        width: 100%;
        border-right: none;
        border-bottom: 1px solid #e5e7eb;
    }

    .acd-subgrid {
        grid-template-columns: 1fr;
    }
}
</style>